import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Stack,
  Paper,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  ApiError,
  ExternalLinkRequest,
  ExternalLinkResponse,
  Product,
  ProductTariff,
  ProductsResponse,
  fetchProducts,
  getExternalLink,
} from '../api/rustore';

type StatusColor = 'default' | 'success' | 'warning' | 'info' | 'error';

const getStatusColor = (status?: string): StatusColor => {
  const value = status?.toLowerCase();
  switch (value) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'warning';
    case 'blocked':
      return 'error';
    default:
      return 'default';
  }
};

const formatTariffPrice = (tariff: ProductTariff): string => {
  const parts = [];
  if (tariff.priceValue !== undefined && tariff.priceValue !== null) {
    parts.push(String(tariff.priceValue));
  }
  if (tariff.priceCurrency) {
    parts.push(tariff.priceCurrency);
  }
  return parts.join(' ');
};

const Products: React.FC = () => {
  const { token, username, logout } = useAuth();
  const navigate = useNavigate();

  const [selectedApp, setSelectedApp] = useState('');
  const [limit, setLimit] = useState('200');
  const [productCode, setProductCode] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [orderId, setOrderId] = useState('');
  const [developerPayload, setDeveloperPayload] = useState('');
  const [appUserId, setAppUserId] = useState('');
  const [platform, setPlatform] = useState<'web' | 'mobile'>('web');
  const [backUrl, setBackUrl] = useState('');
  const [successUrl, setSuccessUrl] = useState('');
  const [failUrl, setFailUrl] = useState('');
  const [externalResult, setExternalResult] = useState<ExternalLinkResponse['data'] | null>(null);
  const [snackbarState, setSnackbarState] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
    {
      open: false,
      message: '',
      severity: 'success',
    }
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const appIdsQuery = useQuery<{ data: string[] }>(
    ['appIDs', token],
    async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/callbacks/rustore/app-ids`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    {
      enabled: Boolean(token),
      staleTime: 5 * 60 * 1000,
    }
  );

  const limitValue = useMemo(() => {
    if (!limit.trim()) {
      return undefined;
    }
    const parsed = Number(limit);
    if (Number.isNaN(parsed) || parsed <= 0) {
      return undefined;
    }
    return parsed;
  }, [limit]);

  const limitError = limit.trim() !== '' && limitValue === undefined;

  const productsQuery = useQuery<ProductsResponse, ApiError>(
    ['products', selectedApp, limitValue, token],
    () =>
      fetchProducts({
        appId: selectedApp,
        limit: limitValue,
        token: token ?? '',
      }),
    {
      enabled: Boolean(token && selectedApp && !limitError),
      keepPreviousData: true,
    }
  );

  const externalLinkMutation = useMutation<ExternalLinkResponse, ApiError, ExternalLinkRequest>(
    (payload) =>
      getExternalLink({
        appId: selectedApp,
        payload,
        token: token ?? '',
      }),
    {
      onMutate: () => {
        setExternalResult(null);
      },
      onSuccess: (response) => {
        setExternalResult(response.data);
        setSnackbarState({
          open: true,
          message: 'External link успешно создан.',
          severity: 'success',
        });
      },
      onError: (error) => {
        setExternalResult(null);
        setSnackbarState({
          open: true,
          message: error.message || 'Не удалось создать external link.',
          severity: 'error',
        });
      },
    }
  );

  const products = productsQuery.data?.data?.elements ?? [];
  const summary = productsQuery.data?.data;

  const handleAppChange = (event: SelectChangeEvent<string>) => {
    setSelectedApp(event.target.value);
    setExternalResult(null);
    setProductCode('');
    setOrderId('');
    setDeveloperPayload('');
    setAppUserId('');
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(event.target.value);
  };

  const handleSelectProduct = (product: Product) => {
    setProductCode(product.productId ?? '');
  };

  const productCodeError = Boolean(selectedApp) && productCode.trim() === '';
  const orderIdError = Boolean(selectedApp) && orderId.trim() === '';
  const appUserIdError = Boolean(selectedApp) && appUserId.trim() === '';
  const quantityError = Boolean(selectedApp) && quantity <= 0;

  const handleSubmitExternal = () => {
    if (!selectedApp) {
      setSnackbarState({
        open: true,
        message: 'Выберите приложение.',
        severity: 'error',
      });
      return;
    }
    if (productCodeError || orderIdError || appUserIdError || quantityError) {
      setSnackbarState({
        open: true,
        message: 'Заполните обязательные поля формы.',
        severity: 'error',
      });
      return;
    }

    const payload: ExternalLinkRequest = {
      productCode,
      quantity,
      orderId,
      developerPayload: developerPayload || undefined,
      appUserId,
      platform,
    };

    const trimmedBackUrl = backUrl.trim();
    const trimmedSuccessUrl = successUrl.trim();
    const trimmedFailUrl = failUrl.trim();

    if (trimmedBackUrl) {
      payload.backUrl = trimmedBackUrl;
    }
    if (trimmedSuccessUrl) {
      payload.successUrl = trimmedSuccessUrl;
    }
    if (trimmedFailUrl) {
      payload.failUrl = trimmedFailUrl;
    }

    externalLinkMutation.mutate(payload);
  };

  const handleResetFilters = () => {
    setLimit('200');
  };

  const handleSnackbarClose = () => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            RuStore API Server — Products
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button color="inherit" onClick={handleGoHome}>
              Home
            </Button>
            <Typography>Пользователь: {username}</Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Каталог продуктов и external-pay
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Просмотр каталога и генерация внешней ссылки оплаты RuStore.
            </Typography>
          </Box>

          <Paper sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Select
                value={selectedApp}
                onChange={handleAppChange}
                displayEmpty
                sx={{ minWidth: 220 }}
                renderValue={(value) => (value ? value : 'Выберите приложение')}
              >
                <MenuItem disabled value="">
                  Выберите приложение
                </MenuItem>
                {appIdsQuery.data?.data.map((appId) => (
                  <MenuItem key={appId} value={appId}>
                    {appId}
                  </MenuItem>
                ))}
              </Select>

              <TextField
                label="Limit"
                type="number"
                value={limit}
                onChange={handleLimitChange}
                error={limitError}
                helperText={limitError ? 'Введите число больше 0' : ' '}
                sx={{ maxWidth: 160 }}
              />

              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={handleResetFilters}>
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={() => productsQuery.refetch()}
                  disabled={!selectedApp || limitError}
                >
                  Refresh
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="stretch">
            <Paper sx={{ p: 3, flex: 1 }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">Каталог</Typography>
                  {summary && (
                    <Typography variant="body2" color="text.secondary">
                      Показано {summary.elementsCount} из {summary.totalElementsCount}
                    </Typography>
                  )}
                </Stack>

                {!selectedApp && <Alert severity="info">Выберите приложение, чтобы загрузить каталог.</Alert>}

                {limitError && <Alert severity="warning">Limit должен быть числом больше 0.</Alert>}

                {productsQuery.isLoading && (
                  <Stack alignItems="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </Stack>
                )}

                {productsQuery.isError && (
                  <Alert severity="error">{productsQuery.error?.message || 'Ошибка загрузки каталога.'}</Alert>
                )}

                {!productsQuery.isLoading && !productsQuery.isError && selectedApp && (
                  <>
                    {summary?.continuationToken && (
                      <Alert severity="info">
                        Получен continuationToken: {summary.continuationToken}
                      </Alert>
                    )}
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Продукт</TableCell>
                            <TableCell>Тип</TableCell>
                            <TableCell>Статус</TableCell>
                            <TableCell>Тарифы</TableCell>
                            <TableCell align="right">Действия</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {products.map((product) => (
                            <TableRow key={product.productId ?? product.name}>
                              <TableCell>
                                <Stack spacing={0.5}>
                                  <Typography variant="subtitle2">{product.name || product.productId}</Typography>
                                  {product.description && (
                                    <Typography variant="body2" color="text.secondary">
                                      {product.description}
                                    </Typography>
                                  )}
                                  {product.productId && (
                                    <Typography variant="caption" color="text.secondary">
                                      {product.productId}
                                    </Typography>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>{product.productType ?? '—'}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={product.status ?? 'unknown'}
                                  color={getStatusColor(product.status)}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                {product.tariffs && product.tariffs.length > 0 ? (
                                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {product.tariffs.map((tariff, index) => {
                                      const labelParts = [tariff.name, formatTariffPrice(tariff)]
                                        .map((value) => value?.trim())
                                        .filter(Boolean);
                                      const label = labelParts.length ? labelParts.join(' · ') : 'Tariff';
                                      return (
                                        <Chip
                                          key={tariff.tariffCode ?? `${product.productId}-${index}`}
                                          size="small"
                                          variant="outlined"
                                          label={label}
                                        />
                                      );
                                    })}
                                  </Stack>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Button size="small" onClick={() => handleSelectProduct(product)}>
                                  Use
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {products.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <Typography variant="body2" color="text.secondary">
                                  Нет данных для отображения.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Stack>
            </Paper>

            <Paper sx={{ p: 3, flexBasis: { xs: 'auto', md: 360 } }}>
              <Stack spacing={2}>
                <Typography variant="h6">External link</Typography>
                <Typography variant="body2" color="text.secondary">
                  Заполните поля и получите ссылку на оплату.
                </Typography>

                <Divider />

                <TextField
                  label="Product code"
                  value={productCode}
                  onChange={(event) => setProductCode(event.target.value)}
                  disabled={!selectedApp}
                  error={productCodeError}
                  helperText={productCodeError ? 'Обязательное поле' : ' '}
                  required
                />
                <TextField
                  label="Quantity"
                  type="number"
                  value={quantity}
                  onChange={(event) => {
                    const nextValue = Number(event.target.value);
                    setQuantity(Number.isNaN(nextValue) ? 0 : nextValue);
                  }}
                  disabled={!selectedApp}
                  error={quantityError}
                  helperText={quantityError ? 'Введите число больше 0' : ' '}
                  required
                />
                <TextField
                  label="Order ID"
                  value={orderId}
                  onChange={(event) => setOrderId(event.target.value)}
                  disabled={!selectedApp}
                  error={orderIdError}
                  helperText={orderIdError ? 'Обязательное поле' : ' '}
                  required
                />
                <TextField
                  label="Developer payload"
                  value={developerPayload}
                  onChange={(event) => setDeveloperPayload(event.target.value)}
                  disabled={!selectedApp}
                />
                <TextField
                  label="App user ID"
                  value={appUserId}
                  onChange={(event) => setAppUserId(event.target.value)}
                  disabled={!selectedApp}
                  error={appUserIdError}
                  helperText={appUserIdError ? 'Обязательное поле' : ' '}
                  required
                />
                <TextField
                  select
                  label="Platform"
                  value={platform}
                  onChange={(event) => setPlatform(event.target.value as 'web' | 'mobile')}
                  disabled={!selectedApp}
                >
                  <MenuItem value="web">web</MenuItem>
                  <MenuItem value="mobile">mobile</MenuItem>
                </TextField>
                <TextField
                  label="Back URL"
                  value={backUrl}
                  onChange={(event) => setBackUrl(event.target.value)}
                  disabled={!selectedApp}
                  placeholder="https://example.com/back"
                />
                <TextField
                  label="Success URL"
                  value={successUrl}
                  onChange={(event) => setSuccessUrl(event.target.value)}
                  disabled={!selectedApp}
                  placeholder="https://example.com/success"
                />
                <TextField
                  label="Fail URL"
                  value={failUrl}
                  onChange={(event) => setFailUrl(event.target.value)}
                  disabled={!selectedApp}
                  placeholder="https://example.com/fail"
                />

                <Button
                  variant="contained"
                  onClick={handleSubmitExternal}
                  disabled={!selectedApp || externalLinkMutation.isLoading}
                >
                  {externalLinkMutation.isLoading ? 'Отправка...' : 'Создать ссылку'}
                </Button>

                {externalResult && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2">Результат</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pay URL:
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {externalResult.payUrl}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Purchase ID: {externalResult.purchaseId}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Invoice ID: {externalResult.invoiceId}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        href={externalResult.payUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Открыть оплату
                      </Button>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Stack>
      </Container>

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarState.severity} variant="filled">
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Products;
